import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, GraduationCap } from 'lucide-react';
import { universityCourseMeta } from '../../data/university';

const UniversitySection = () => {
  const { t } = useTranslation(['university', 'editor']);
  const catalog = (t('university:courses', { returnObjects: true }) || {}) as Record<string, { title: string; description: string }>;
  const featured = universityCourseMeta.slice(0, 3);

  return (
    <section className="relative py-24 bg-slate-950 overflow-hidden">
      <div className="pointer-events-none absolute top-0 left-1/3 w-[700px] h-[360px] rounded-full bg-cyan-600/10 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 border border-cyan-400/25 bg-cyan-400/[0.06] text-cyan-200">
            <GraduationCap className="h-3.5 w-3.5" />
            <span className="text-sm font-semibold tracking-wide">{t('university:home.badge')}</span>
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold mb-4 text-white">{t('university:home.title')}</h2>
          <p className="text-lg max-w-2xl mx-auto text-slate-400">{t('university:home.subtitle')}</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((course, index) => {
            const Icon = course.icon;
            const data = catalog[course.id];
            if (!data) return null;
            return (
              <motion.div key={course.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }}>
                <Link
                  to={`/university/${course.id}`}
                  className="group flex flex-col h-full rounded-3xl p-7 bg-white/[0.03] border border-white/10 hover:bg-white/[0.05] hover:border-cyan-400/40 transition-all"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500/25 to-cyan-400/10 border border-white/10 flex items-center justify-center mb-5">
                    <Icon className="h-5 w-5 text-cyan-300" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{data.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed flex-1">{data.description}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-300 group-hover:text-white">
                    {t('university:home.exploreCourse')}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/university"
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] hover:border-cyan-400/40 transition-all"
          >
            {t('university:home.viewAll')}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default UniversitySection;
